import { useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  CANVAS_EXTENT,
  buildBezierPath,
  connectionKey,
  getInputPoint,
  getOutputPoint,
} from './flowGeometry';

// Label drawn on the curve of a branch connection so the two paths of a
// conditional split stay readable when the blocks are moved apart.
const BRANCH_LABELS = { yes: 'Ja', no: 'Nein' };

// SVG layer drawing all connections as cubic bezier curves with arrowheads,
// plus the live line while a new connection is being dragged.
const ConnectionLayer = ({
  blocks,
  connections,
  blockSizes,
  selectedKey,
  liveLine,
  readOnly,
  onSelect,
  onDelete,
}) => {
  const theme = useTheme();
  const blockMap = useMemo(() => new Map(blocks.map(block => [block.blockId, block])), [blocks]);

  const baseColor = theme.palette.mode === 'light' ? theme.palette.grey[500] : theme.palette.grey[600];
  const selectedColor = theme.palette.primary.main;

  return (
    <svg
      width={CANVAS_EXTENT}
      height={CANVAS_EXTENT}
      style={{ position: 'absolute', top: 0, left: 0, overflow: 'visible', pointerEvents: 'none' }}
    >
      <defs>
        <marker
          id='fz-flow-arrow'
          markerWidth='10'
          markerHeight='10'
          refX='7'
          refY='4'
          orient='auto'
          markerUnits='userSpaceOnUse'
        >
          <path d='M0,0 L8,4 L0,8 Z' fill={baseColor} />
        </marker>
        <marker
          id='fz-flow-arrow-selected'
          markerWidth='10'
          markerHeight='10'
          refX='7'
          refY='4'
          orient='auto'
          markerUnits='userSpaceOnUse'
        >
          <path d='M0,0 L8,4 L0,8 Z' fill={selectedColor} />
        </marker>
      </defs>
      {connections.map(connection => {
        const fromBlock = blockMap.get(connection.from);
        const toBlock = blockMap.get(connection.to);
        if (!fromBlock || !toBlock) return null;
        const start = getOutputPoint(fromBlock, blockSizes, connection.branch);
        const end = getInputPoint(toBlock);
        // Stop slightly above the input port so the arrow tip stays visible.
        const path = buildBezierPath(start, { x: end.x, y: end.y - 5 });
        const key = connectionKey(connection);
        const selected = key === selectedKey;
        const mid = { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 };
        const branchLabel = BRANCH_LABELS[connection.branch];
        return (
          <g key={key}>
            <path
              d={path}
              fill='none'
              stroke='transparent'
              strokeWidth={16}
              style={{ pointerEvents: 'stroke', cursor: 'pointer' }}
              onPointerDown={event => event.stopPropagation()}
              onClick={event => {
                event.stopPropagation();
                onSelect(key);
              }}
            />
            <path
              d={path}
              fill='none'
              stroke={selected ? selectedColor : baseColor}
              strokeWidth={selected ? 2.5 : 2}
              markerEnd={`url(#${selected ? 'fz-flow-arrow-selected' : 'fz-flow-arrow'})`}
            />
            {branchLabel && (
              <text
                x={start.x}
                y={start.y + 30}
                textAnchor='middle'
                fontSize='11'
                fontWeight='600'
                fill={selected ? selectedColor : baseColor}
                style={{ pointerEvents: 'none' }}
              >
                {branchLabel}
              </text>
            )}
            {selected && !readOnly && (
              <g
                transform={`translate(${mid.x}, ${mid.y})`}
                style={{ pointerEvents: 'all', cursor: 'pointer' }}
                onPointerDown={event => event.stopPropagation()}
                onClick={event => {
                  event.stopPropagation();
                  onDelete(key);
                }}
              >
                <circle
                  r='11'
                  fill={theme.palette.background.paper}
                  stroke={theme.palette.error.main}
                  strokeWidth='1.5'
                />
                <path
                  d='M-4,-4 L4,4 M-4,4 L4,-4'
                  stroke={theme.palette.error.main}
                  strokeWidth='2'
                  strokeLinecap='round'
                />
              </g>
            )}
          </g>
        );
      })}
      {liveLine && (
        <path
          d={buildBezierPath(liveLine.from, liveLine.to)}
          fill='none'
          stroke={selectedColor}
          strokeWidth={2}
          strokeDasharray='6 4'
          markerEnd='url(#fz-flow-arrow-selected)'
        />
      )}
    </svg>
  );
};

export default ConnectionLayer;
